import store from '@state/store'
import Cookies from 'js-cookie'

export default [
  {
    path: '/404',
    name: '404',
    component: require('@views/_404.vue').default,
    // Allows props to be passed to the 404 page through route
    // params, such as `resource` to define what wasn't found.
    props: true,
  },
  {
    path: '',
    name: 'company',
    component: () => lazyLoadView(import('@views/input-company-id.vue')),
  },
  {
    path: '/:companyId',
    component: () => lazyLoadView(import('@layouts/main.vue')),
    meta: {
      authRequired: true,
    },
    beforeEnter: (to, from, next) => {
      checkValidCompanyId(to.redirectedFrom || to.params?.companyId, next)
    },
    children: [
      {
        path: 'reservation',
        name: 'reservation',
        component: () =>
          lazyLoadView(import('@views/reservation/reservation.vue')),
      },
      {
        path: 'cooperation',
        name: 'cooperation',
        component: () =>
          lazyLoadView(import('@/router/views/settingParams/cooperation.vue')),
      },
      {
        path: 'target-door-settings',
        name: 'targetDoorSettings',
        component: () =>
          lazyLoadView(
            import('@/router/views/targetDoorSetting/target-door-settings.vue')
          ),
      },
      {
        path: 'confirm-email-resend/:id',
        name: 'emailResend',
        component: () =>
          lazyLoadView(import('@views/reservation/emailResent.vue')),
      },
      {
        path: 'email-resend/:id',
        name: 'emailResend',
        component: () =>
          lazyLoadView(import('@views/reservation/emailResentSuccess.vue')),
      },
    ],
  },
  {
    path: '/:companyId/login',
    component: () => lazyLoadView(import('@layouts/main-card.vue')),
    beforeEnter: (to, from, next) => {
      checkValidCompanyId(to.params?.companyId, next)
    },
    children: [
      {
        path: '',
        component: () => lazyLoadView(import('@views/login.vue')),
        name: 'login',
        meta: {
          beforeResolve(routeTo, routeFrom, next) {
            // If the user is already logged in
            if (store.getters['auth/loggedIn']) {
              // Redirect to the home page instead
              next({
                name: 'reservation',
                params: { companyId: store.getters['auth/companyId'] },
              })
            } else {
              // Continue to the login page
              next()
            }
          },
        },
      },
    ],
  },
  {
    path: '/:companyId/logout',
    name: 'logout',
    beforeEnter: (to, from, next) => {
      checkValidCompanyId(to.params?.companyId, next)
    },
    meta: {
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        store.dispatch('auth/logOut')
        next({
          name: 'login',
          params: {
            companyId:
              routeTo.params?.companyId || store.getters['auth/companyId'],
          },
        })
      },
    },
  },
  {
    path: '/booking-info',
    component: () => lazyLoadView(import('@layouts/main-card.vue')),
    children: [
      {
        path: 'search-booking',
        component: () =>
          lazyLoadView(
            import('@views/resendMailForEndUser/resendMailForEndUser.vue')
          ),
        name: 'searchBooking',
      },
      {
        path: 'resend-mail',
        component: () =>
          lazyLoadView(
            import('@views/resendMailForEndUser/resendMailEndUserSuccess.vue')
          ),
        name: 'resendMail',
      },
      {
        path: 'resend-secret',
        name: 'resendQr',
        component: () => lazyLoadView(import('@views/qrSecret/resendQr.vue')),
      },
      {
        path: 'confirm-resend',
        name: 'confirmResendQr',
        component: () =>
          lazyLoadView(import('@views/qrSecret/confirmResendQr.vue')),
      },
    ],
  },
  {
    path: '/booking-info/secret',
    name: 'qrCode',
    component: () => lazyLoadView(import('@views/qrSecret/qrSecret.vue')),
  },
  // Redirect any unmatched routes to the 404 page. This may
  // require some server configuration to work in production:
  // https://router.vuejs.org/en/essentials/history-mode.html#example-server-configurations
  {
    path: '*',
    redirect: '404',
  },
]

// Lazy-loads view components, but with better UX. A loading view
// will be used if the component takes a while to load, falling
// back to a timeout view in case the page fails to load. You can
// use this component to lazy-load a route with:
//
// component: () => lazyLoadView(import('@views/my-view'))
//
// NOTE: Components loaded with this strategy DO NOT have access
// to in-component guards, such as beforeRouteEnter,
// beforeRouteUpdate, and beforeRouteLeave. You must either use
// route-level guards instead or lazy-load the component directly:
//
// component: () => import('@views/my-view')
//
function lazyLoadView(AsyncView) {
  const AsyncHandler = () => ({
    component: AsyncView,
    // A component to use while the component is loading.
    loading: require('@views/_loading.vue').default,
    // Delay before showing the loading component.
    // Default: 200 (milliseconds).
    delay: 400,
    // A fallback component in case the timeout is exceeded
    // when loading the component.
    error: require('@views/_timeout.vue').default,
    // Time before giving up trying to load the component.
    // Default: Infinity (milliseconds).
    timeout: 10000,
  })

  return Promise.resolve({
    functional: true,
    render(h, { data, children }) {
      // Transparently pass any props or children
      // to the view component.
      return h(AsyncHandler, data, children)
    },
  })
}

/**
 * check valid company id
 * if cookies is clear
 */

function checkValidCompanyId(id, next) {
  const idoorsCredentialCookies = Cookies.getJSON('idoors-credential')
  const companyId = idoorsCredentialCookies?.companyId
  if (!store.getters['auth/companyId'] && !companyId) {
    store.commit('auth/SET_CURRENT_COMPANY_ID', id)
    next({ name: 'login', params: { companyId: id } })
    return
  }
  if (id !== store.getters['auth/companyId']) {
    next({ name: 'company' })
    return
  }
  next()
}
